import { Add } from '@mui/icons-material';
import cn from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { formatIsoDuration } from '@/tracker/entities/worklog';

import { WorklogFormResult } from '../WorklogForm';
import { WorklogFormDialog } from '../WorklogFormDialog';
import { WorklogHistoryDialog } from '../WorklogHistoryDialog';
import { IssueCardStore } from '../../model/IssueCardStore';

import { getProgress } from './getProgress';

import s from './WorklogSection.module.scss';

type Props = {
  store: IssueCardStore;
  currentUserId: string | null;
  /** Админ удаляет любые логи, остальные — только свои */
  isAdmin: boolean;
};

/** Блок «Учёт времени» в панели карточки: полоса прогресса, запись времени и история записей */
const WorklogSection: React.FC<Props> = observer(({ store, currentUserId, isAdmin }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const issue = store.issue;

  const handleSubmit = async (data: WorklogFormResult) => {
    if (await store.addWorklog(data)) setIsFormOpen(false);
  };

  if (!issue) return null;

  const progress = getProgress(issue);

  return (
    <div className={s.block}>
      {progress && (
        <div
          className={s.progress}
          role="progressbar"
          aria-label="Затрачено от оценки"
          aria-valuenow={progress.percent}
          title={`Затрачено ${formatIsoDuration(issue.spent ?? 'PT0M')} · ${progress.percent}%`}
        >
          <div
            className={cn(s.progress__bar, progress.isOverrun && s.progress__bar_overrun)}
            style={{ width: `${progress.percent}%` }}
          />
        </div>
      )}
      <div className={s.block__actions}>
        {!store.isReadOnly && (
          <button type="button" className={s.block__action} onClick={() => setIsFormOpen(true)}>
            <Add fontSize="inherit" />
            Записать время
          </button>
        )}
        <button type="button" className={s.block__action} onClick={() => setIsHistoryOpen(true)}>
          История записей
        </button>
      </div>

      <AnimatePresence>
        {isHistoryOpen && (
          <WorklogHistoryDialog
            store={store}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            onAdd={() => setIsFormOpen(true)}
            onClose={() => setIsHistoryOpen(false)}
          />
        )}
      </AnimatePresence>

      <WorklogFormDialog
        open={isFormOpen}
        title={`Учёт времени · ${issue.key}`}
        estimation={issue.estimation}
        disabled={store.isSaving}
        onSubmit={handleSubmit}
        onCancel={() => setIsFormOpen(false)}
      />
    </div>
  );
});

export { WorklogSection };
